import AdminService from 'external-services/diary-admin-service/admin.service';
import DateUtilsService from 'external-services/date-utils-service/index';
import moment from 'moment';
import {
  BookingValidationItems,
  ITabValidationRules,
  ITabValidationSwitches,
  ValidationErrorType,
  BookingCreatorPanelType,
  IValidationErrorListItem,
} from 'external-services/booking-validation-service/bookingValidation.types';
import {
  bookingStatusType,
  BookingType,
  IBooking,
  IBookingLink,
  IPaymentDetailsBase,
  ITableDate,
  servicePaymentType,
} from 'shared-types/index';
import { isEmpty } from 'lodash-es';
import * as EmailValidator from 'email-validator';

const NS = 'BookingValidationService';

class BookingValidationService {
  /**
   * Validation rules for new booking details tab.
   * 'validationList' order is important - only first item gets scrolled to
   */
  public getBookingDetailsValidation(
    booking: IBooking,
    selectedTime: { open: boolean },
    includeStandbyCheck = false,
    includeStaffCheck = false,
    validationList: BookingValidationItems[] = null,
    skipTimeValidation = false
  ): boolean {
    const hasTables = !!(booking.tables && booking.tables.length);

    const tablesValid: boolean =
      hasTables ||
      (includeStandbyCheck && booking.bookingType === BookingType.StandbyList) ||
      booking.bookingType === BookingType.WaitList;

    if (!tablesValid && validationList) {
      if (!booking.people) validationList.push(BookingValidationItems.people);
      if (!tablesValid) validationList.push(BookingValidationItems.tables);

      if (!skipTimeValidation) {
        if (!selectedTime || !selectedTime.open)
          validationList.push(BookingValidationItems.selectedTime);
      }
    }

    if (includeStaffCheck) {
      if (validationList) validationList.unshift(BookingValidationItems.staffName);
      return false;
    }

    const isTimeValid =
      skipTimeValidation || (!skipTimeValidation && selectedTime && selectedTime.open);
    return !!(booking.time && booking.people && isTimeValid && tablesValid);
  }

  /**
   * Scrolls to where the validation message is displayed when using tabs in new booking.
   * Only necessary for first tab, so includes check for selectedTab = 0
   */
  public scrollToValidationError(
    selectedTab: number,
    validationList: BookingValidationItems[] = null,
    restrictedItems: BookingValidationItems[] = null
  ): void {
    if (!validationList || !validationList.length || selectedTab > 0) {
      return;
    }
    if (restrictedItems) {
      validationList = validationList.filter((item) => restrictedItems.includes(item));
    }
    if (!validationList.length) {
      return;
    }

    const tabElement: HTMLElement = document.body.querySelector('md-tab-content');
    if (!tabElement) {
      return;
    }

    let isAtTop = true;
    switch (validationList[0]) {
      case BookingValidationItems.people:
      case BookingValidationItems.staffName:
        isAtTop = true;
        break;

      case BookingValidationItems.tables:
      case BookingValidationItems.selectedTime:
        isAtTop = false;
        break;
    }

    // timeout just gives dom time to render
    setTimeout(() => {
      tabElement.scrollTo({ top: isAtTop ? 0 : 5000, behavior: 'smooth' }); // large number scrolls to bottom
    }, 500);
  }

  public setUpsellOptionsValidity(activePaymentDetails: IPaymentDetailsBase): {
    isUpsellOptions: boolean;
    upsellOptionsValid: boolean;
  } {
    let isUpsellOptions = false;
    let upsellOptionsValid = false;

    if (!isEmpty(activePaymentDetails.menuOptionsUpsell)) {
      isUpsellOptions = true;
    } else {
      upsellOptionsValid = true;
    }

    return {
      isUpsellOptions,
      upsellOptionsValid,
    };
  }

  public canSaveNewBookings({
    detailsValid,
    customerValid,
    paymentValid,
    guestListValid,
    bookingDateTime,
    bookingToMoveDateTime,
  }: {
    detailsValid: boolean;
    customerValid: boolean;
    paymentValid: boolean;
    guestListValid: boolean;
    bookingDateTime?: Date;
    bookingToMoveDateTime?: Date;
  }): boolean {
    return (
      detailsValid &&
      (customerValid || bookingToMoveDateTime) &&
      paymentValid &&
      guestListValid &&
      this.isValidBookingDate(bookingDateTime, bookingToMoveDateTime)
    );
  }

  private isValidBookingDate(bookingDateTime?: Date, bookingToMoveDateTime?: Date): boolean {
    if (bookingToMoveDateTime && bookingDateTime) {
      return bookingDateTime.toLocaleDateString() !== bookingToMoveDateTime.toLocaleDateString();
    }
    return true;
  }

  public setBookingOptionsValidity(
    activePaymentDetails: IPaymentDetailsBase,
    bookingPax: number
  ): {
    isBookingOption: boolean;
    bookingOptionsValid: boolean;
  } {
    let isBookingOption = false;
    let bookingOptionsValid = false;

    const isBOValid = activePaymentDetails.peopleRequired <= bookingPax;

    if (!isEmpty(activePaymentDetails.menuOptions) && isBOValid) {
      isBookingOption = true;
      bookingOptionsValid = false;
    } else {
      bookingOptionsValid = true;
    }

    return {
      isBookingOption,
      bookingOptionsValid,
    };
  }

  public checkForUpsellValidation({
    isUpsellOptions,
    activePaymentDetails,
    upsellOptionsValid,
    upsellPageSkip,
    showUpsellOptions,
  }: {
    isUpsellOptions: boolean;
    activePaymentDetails: IPaymentDetailsBase;
    upsellOptionsValid: boolean;
    upsellPageSkip: boolean;
    showUpsellOptions: boolean;
  }): boolean {
    return (
      !isUpsellOptions ||
      upsellOptionsValid ||
      isEmpty(activePaymentDetails.menuOptionsUpsell) ||
      !showUpsellOptions ||
      upsellPageSkip
    );
  }

  public isBookingPaymentValid(paymentDetails: IPaymentDetailsBase): boolean {
    return !!(
      paymentDetails &&
      paymentDetails.paymentType &&
      (paymentDetails.paymentType === servicePaymentType.noPayment || paymentDetails.price)
    );
  }

  /**
   * Checks validation rules before saving new booking
   */

  // Todo add unit tests

  public getTabValidationRules(
    booking: IBooking,
    selectedTime: ITableDate,
    tabsLength: number,
    {
      customerValid,
      detailsValid,
      paymentValid,
      isCovidGuestMode,
      guestListValid,
      bookingOptionsValid,
      isBookingOption,
      isRecurringBooking,
    }: ITabValidationSwitches,
    includeStandbyCheck = false,
    includeStaffCheck = false
  ): ITabValidationRules {
    let forceGuestListValidation = false;
    let selectedTab = 1;
    let displayErrors = 2;

    if (!isRecurringBooking) {
      if (
        !this.getBookingDetailsValidation(
          booking,
          selectedTime,
          includeStandbyCheck,
          includeStaffCheck
        )
      ) {
        selectedTab = 0;
        displayErrors = 1;
      } else if (customerValid && detailsValid && !paymentValid) {
        selectedTab = tabsLength - 1;
        displayErrors = 2;
      } else if (
        isCovidGuestMode &&
        customerValid &&
        detailsValid &&
        paymentValid &&
        !guestListValid
      ) {
        selectedTab = 5;
        forceGuestListValidation = true;
      } else {
        selectedTab = 1;
        displayErrors = 2;
      }
    } else {
      if (
        !this.getBookingDetailsValidation(
          booking,
          selectedTime,
          includeStandbyCheck,
          includeStaffCheck
        )
      ) {
        selectedTab = 0;
        displayErrors = 1;
      } else if (customerValid && detailsValid && !paymentValid) {
        selectedTab = tabsLength - 1;
        displayErrors = 2;
      } else if (
        isCovidGuestMode &&
        customerValid &&
        detailsValid &&
        paymentValid &&
        !guestListValid
      ) {
        selectedTab = 6;
        forceGuestListValidation = true;
      } else {
        selectedTab = 2;
        displayErrors = 2;
      }
    }

    return { forceGuestListValidation, selectedTab, displayErrors };
  }

  public isMovedBookingSameTime(bookingDateTime: Date, bookingToMoveDateTime?: Date): boolean {
    return (
      bookingToMoveDateTime &&
      bookingDateTime.toDateString() === bookingToMoveDateTime.toDateString()
    );
  }

  /**
   * Filter customer bookings to exclude the bookings with the below statuses:
   * bookingType: StandbyList or WaitList
   * statusType: cancelled or standby-pending
   */
  public isValidPastBooking(booking: IBooking): boolean {
    if (booking.bookingType === BookingType.StandbyList) return false;

    if (booking.bookingType === BookingType.WaitList) return false;

    // Bookings with status name as "Cancelled" and "No show", have statusType as "cancelled"
    if (booking.status.statusType === bookingStatusType.cancelled) return false;

    if (booking.status.statusType === bookingStatusType.standbyPending) return false;

    const timezoneId = AdminService.getVenueTimezoneId();
    // Get venue current time
    const venueCurrentTime = DateUtilsService.getVenueTime(timezoneId);

    // Exclude future bookings
    if (moment(venueCurrentTime).isBefore(booking.time)) return false;

    return true;
  }

  /**
   * Determines the booking links which have valid value.
   */
  public determineValidBookingLinks(bookingLinks: IBookingLink[]): IBookingLink[] {
    // Do not consider the links which are empty.
    return bookingLinks.filter((link) => !!(link.linkName || link.linkURL));
  }

  public getValidationMessages(
    bookingValidationItems: BookingValidationItems[]
  ): IValidationErrorListItem[] {
    const cantSaveMessageList: IValidationErrorListItem[] = [];
    bookingValidationItems.forEach((item) => {
      switch (item) {
        case BookingValidationItems.staffName:
          cantSaveMessageList.push({
            msg: 'Staff name required.',
            type: ValidationErrorType.staff,
            panel: BookingCreatorPanelType.staff,
          });
          break;

        case BookingValidationItems.tables:
          cantSaveMessageList.push({
            msg: 'Table required.',
            type: ValidationErrorType.table,
            panel: BookingCreatorPanelType.bookingDetails,
          });
          break;

        case BookingValidationItems.selectedTime:
          cantSaveMessageList.push({
            msg: 'Time required.',
            type: ValidationErrorType.time,
            panel: BookingCreatorPanelType.bookingDetails,
          });
          break;

        case BookingValidationItems.people:
          cantSaveMessageList.push({
            msg: 'PAX required.',
            type: ValidationErrorType.pax,
            panel: BookingCreatorPanelType.bookingDetails,
          });
          break;

        case BookingValidationItems.recurringBookingNotConfirmed:
          cantSaveMessageList.push({
            msg: 'Press ‘DONE’ on Recurring booking to confirm settings.',
            type: ValidationErrorType.recurringBooking,
            panel: BookingCreatorPanelType.recurringBooking,
          });
          break;

        case BookingValidationItems.bookingOptions:
          cantSaveMessageList.push({
            msg: 'Booking options are invalid.',
            type: ValidationErrorType.bookingOptions,
            panel: BookingCreatorPanelType.bookingOptions,
          });
          break;
        case BookingValidationItems.bookingOptionsPax:
          cantSaveMessageList.push({
            msg: "Pax change doesn't meet the requirement for an option added. Please remove the options.",
            type: ValidationErrorType.bookingOptions,
            panel: BookingCreatorPanelType.bookingOptions,
          });
          break;

        case BookingValidationItems.upsellOptions:
          cantSaveMessageList.push({
            msg: 'Upsell options are invalid.',
            type: ValidationErrorType.upsellOptions,
            panel: BookingCreatorPanelType.upsellOptions,
          });
          break;
        case BookingValidationItems.notChangedStatus:
          cantSaveMessageList.push({
            msg: 'No status update.',
            type: ValidationErrorType.status,
            panel: BookingCreatorPanelType.status,
          });
          break;
      }
    });

    return cantSaveMessageList;
  }

  public validateEmail(email: string): boolean {
    return EmailValidator.validate(email);
  }
}

const instance = new BookingValidationService();
export default instance as BookingValidationService;
